import { useEffect, useRef, useState } from "react";
import LiveText from "./LiveText";
import StaticText from "./StaticText";
import SearchInput from "./SearchInput";
import TriggerInput from "./TriggerInput";
import LiveWelcomeText from "./LiveWelcomeText";
import { getKeywords, getCascadingKeywords } from "./Requests";
import LiveInfoText from "./LiveInfoText";
import messages from "./Messages";

function getPlaceholders(scale) {
  return messages[
    scale === "long" ? "long_placeholders" : "short_placeholders"
  ];
}

function getTexts(scale) {
  return messages[scale === "long" ? "long_texts" : "short_texts"];
}

const SearchBar = ({
  setActivateTrigger,
  setRequestKeywords,
  setActiveRefresh,
  initSearch,
  setInitSearch,
}) => {
  const [textIndex, setTextIndex] = useState(0);
  const [tempTextIndex, setTempTextIndex] = useState(0);
  const [staticTextArray, setStaticTextArray] = useState([]);
  const [placeholderArray, setPlaceholderArray] = useState({});
  const [displayInput, setDisplayInput] = useState(false);
  const [displayTriggerInput, setDisplayTriggerInput] = useState(false);
  const [keywordsData, setKeywordsData] = useState({});
  
  // Live texts
  const [welcomeTextCompleted, setWelcomeTextCompleted] = useState(false);
  const [typingEffectActive, setTypingEffectActive] = useState(false);
  const [hideWelcomeText, setHideWelcomeText] = useState(true);
  const [hideLoadingText, setHideLoadingText] = useState(false);
  const [hideErrorText, setHideErrorText] = useState(true);

  const scaleOfMonument = useRef("long"); // long, short, ""

  useEffect(() => {
    if (!initSearch) return;
    setTextIndex(0);
    setTempTextIndex(1);
    setStaticTextArray([]);
    setPlaceholderArray({});
    setDisplayTriggerInput(false);
    setInitSearch(false);
    checkDisplayInput();
  }, [initSearch]);

  // Initial load: Get all available keywords for the first dropdowns
  useEffect(() => {
    async function sendRequest() {
      const response = await getKeywords();
      console.log("Initial keywords loaded:", response);
      if (response) {
        setKeywordsData(response);
        setTimeout(() => {
          setHideLoadingText(true);
          setHideWelcomeText(false);
        }, 2000);
      } else {
        setTimeout(() => {
          setHideErrorText(false);
        }, 2000);
      }
    }
    sendRequest();
  }, []);

useEffect(() => {
    // Skip if there are no selections yet
    if (Object.keys(placeholderArray).length === 0) return;

    async function updateOptions() {
      // Pass the entire placeholderArray state to the backend
      const data = await getCascadingKeywords(placeholderArray);
      
      if (data) {
        console.log("Cascading response from backend:", data);
        // Merge the new filtered data with existing to prevent unselected dropdowns from crashing
        setKeywordsData((prev) => ({ ...prev, ...data })); 
      }
    }

    updateOptions();
  }, [placeholderArray]);

  useEffect(() => {
    if (hideWelcomeText || tempTextIndex > 0) return;
    function keyDownHandler(e) {
      if (e.key === "Enter") {
        setHideWelcomeText(true);
        setTypingEffectActive(true);
      }
    }
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [welcomeTextCompleted]);

  useEffect(() => {
    function keyDownHandler(e) {
      if (e.key === "Enter" && displayTriggerInput) {
        setRequestKeywords(getPlaceholders(scaleOfMonument.current));
      }
    }
    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [displayTriggerInput]);

  useEffect(() => {
    if (textIndex === 0) return;
    setStaticTextArray([
      ...staticTextArray,
      getTexts(scaleOfMonument.current)[textIndex - 1],
    ]);
    setPlaceholderArray(getPlaceholders(scaleOfMonument.current));
  }, [textIndex]);

  function setLiveTextIndex(action) {
    if (action === "next") {
      setTextIndex(tempTextIndex);
      setDisplayInput(false);
    }
  }

  function checkDisplayInput() {
    if (textIndex !== getTexts(scaleOfMonument.current).length - 1) {
      setDisplayInput(true);
      setDisplayTriggerInput(false);
    } else {
      setDisplayTriggerInput(true);
      setActivateTrigger(true);
      setTypingEffectActive(false);
    }
  }

  function setPlaceholdersValues(key, value) {
    if (key === "scaleOfMonument") {
      if (value === "a single entity") {
        scaleOfMonument.current = "short";
      } else if (value === "the entire archaeological site") {
        scaleOfMonument.current = "long";
      }

      if (
        placeholderArray.scaleOfMonument !== "" &&
        placeholderArray.scaleOfMonument !== value
      ) {
        const newPlaceholderArray = {
          country: placeholderArray.country,
          scaleOfMonument: value,
          pilotArea: placeholderArray.pilotArea,
        };

        setPlaceholderArray(newPlaceholderArray);
        setStaticTextArray(staticTextArray.slice(0, 2));
        setTextIndex(3);
        setTempTextIndex(3);
      }
    }
    if (Object.keys(messages.short_placeholders).includes(key)) {
      messages.short_placeholders[key] = value;
      setPlaceholderArray((prev) => ({ ...prev, [key]: value }));
    }
    if (Object.keys(messages.long_placeholders).includes(key)) {
      messages.long_placeholders[key] = value;
      setPlaceholderArray((prev) => ({ ...prev, [key]: value }));
    }
    setActiveRefresh(true);
  }

  const currentPlaceholderKey = getTexts(scaleOfMonument.current)[textIndex] 
    ? Object.keys(getPlaceholders(scaleOfMonument.current))[textIndex] 
    : null;

  const currentKeywordsList = (keywordsData && currentPlaceholderKey) 
    ? keywordsData[currentPlaceholderKey] 
    : [];

  return (
    <section className="dss-searchbar">
      <div className="dss-searchbar-container">
        {!hideLoadingText && (
          <LiveInfoText
            textInput={
              hideErrorText ? messages.fetchingText : messages.errorMessage
            }
          />
        )}
        {!hideWelcomeText && (
          <LiveWelcomeText
            textInput={messages.welcomeText}
            setWelcomeTextCompleted={setWelcomeTextCompleted}
          />
        )}
        {!!staticTextArray && hideWelcomeText && (
          <StaticText
            textArray={staticTextArray}
            inputArray={placeholderArray}
            keywordsData={keywordsData}
            setPlaceholdersValues={setPlaceholdersValues}
            setLiveTextIndex={setLiveTextIndex}
            setDisplayTriggerInput={setDisplayTriggerInput}
          />
        )}
        {getTexts(scaleOfMonument.current)[textIndex] &&
          hideWelcomeText &&
          hideLoadingText && (
            <LiveText
              textIndex={textIndex}
              textInput={getTexts(scaleOfMonument.current)[textIndex]}
              setTempTextIndex={setTempTextIndex}
              displayInput={displayInput}
              checkDisplayInput={checkDisplayInput}
              placeholderArray={placeholderArray}
            />
          )}
        {displayInput && hideWelcomeText && !!keywordsData && (
          <SearchInput
            key={textIndex}
            placeholder={
              Object.keys(getPlaceholders(scaleOfMonument.current))[textIndex]
            }
            placeholderKey={
              Object.keys(getPlaceholders(scaleOfMonument.current))[textIndex]
            }
            input={true}
            setLiveTextIndex={setLiveTextIndex}
            setPlaceholdersValues={setPlaceholdersValues}
            keywordsList={currentKeywordsList}
            setDisplayTriggerInput={setDisplayTriggerInput}
          />
        )}
        {displayTriggerInput && <TriggerInput />}
        {typingEffectActive && <div className="dss-search-texting-end" />}
      </div>
    </section>
  );
};

export default SearchBar;